// ── PLOW grid cell layout: how a day's assignment sections read ──────────────
//
// A board usually has two assignment sections (AM and PM). Most days both
// carry the same job, and repeating the chip twice only adds noise, so the
// cell shows it once with the traffic light drawn as a border around the
// whole day. When the sections carry different tasks, each one is its own
// box with its own border, so no status is hidden behind another.
//
// Pure functions of the section list and the day's slots; the grid cell and
// its tests share them.

const RAG_ORDER = { red: 3, amber: 2, green: 1 };

/** The most urgent traffic light in a list: red over amber over green; null when none is set. */
export function worstRag(rags) {
  let best = null;
  for (const r of rags || []) {
    if (r && RAG_ORDER[r] && (!best || RAG_ORDER[r] > RAG_ORDER[best])) best = r;
  }
  return best;
}

const sameTask = (a, b) =>
  !!a?.job && !!b?.job && a.job.id === b.job.id && (a.note || '') === (b.note || '');

/**
 * Group a day's assignment sections for the grid cell.
 *
 * Returns { collapsed, rag, items }. `items` is one { sec, slot } per
 * assignment section, in section order, or a single entry when every
 * section carries the same job and note (`collapsed`). `rag` is the
 * whole-day border: the worst light across the sections when collapsed,
 * null otherwise (each item then carries its own via slot.rag).
 */
export function groupAssignments(sections, slots) {
  const items = (sections || [])
    .filter(s => s.kind === 'assignment')
    .map(sec => ({ sec, slot: (slots || {})[sec.id] }));
  const collapsed = items.length > 1 && items.every(i => sameTask(items[0].slot, i.slot));
  if (!collapsed) return { collapsed: false, rag: null, items };
  const rag = worstRag(items.map(i => i.slot?.rag));
  const first = items[0];
  return { collapsed: true, rag, items: [{ sec: first.sec, slot: { ...first.slot, rag } }] };
}
